import React, {
  useEffect, useRef,
} from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import _ from 'lodash';
import { useIntl } from 'react-intl';

import { showModal } from '../../../actions';
import {
  fetchFileEditions,
  setFileEditions,
} from '../../../actions/Files';

import { canBeUsedImageEditor } from '../../../helpers/Files';

import FileEditionsItem from '../EditionsItem';
import FileSidebarEmptyState from './EmptyState';

import Loading from '../../Loading';

const FileEditions = ({
  file,
  fileEdition,
  handleFetchEditions,
  handleSetEditions,
  switchEdition,
  editions,
  editionsLoading,
  handleFullEditionsLoaded,
}) => {
  const intl = useIntl();

  const prevFileRef = useRef();

  const fileImageEditorSupported = canBeUsedImageEditor(file);

  useEffect(() => {
    if (!_.isEqual(prevFileRef.current, file)) {
      handleFetchEditions(file.uuid).then(() => {
        handleFullEditionsLoaded(true);
      });
    }

    prevFileRef.current = file;

    return () => {
      handleFullEditionsLoaded(false);
    };
  }, [file, handleFetchEditions, handleFullEditionsLoaded]);

  const handleEditionChangeSearchable = (editionToUpdate) => {
    if (fileEdition && editionToUpdate && fileEdition.uuid === editionToUpdate.uuid) {
      switchEdition(editionToUpdate);
    }

    const newEditions = editions.map(edition => {
      if (edition.uuid === editionToUpdate.uuid) {
        return {
          ...edition,
          searchable: editionToUpdate.searchable,
        };
      }
      return edition;
    });

    handleSetEditions(newEditions);
  };

  if (!file) return null;

  return (
    <div className="file-editions file-details-item">
      <div className="file-detail-header file-detail-header-light">
        <span>
          {intl.formatMessage({ defaultMessage: 'Editions' })}
          {(!editionsLoading && fileImageEditorSupported) && <>{' '}{`(${editions.length})`}</>}
        </span>
      </div>
      <div className="file-detail-body file-detail-body-light">
        {fileImageEditorSupported && (
          <>
            <div className="file-editions-body">
              {editionsLoading && (
                <Loading />
              )}
              {!editionsLoading && editions.map(editionFile => (
                <FileEditionsItem
                  key={editionFile.uuid}
                  file={editionFile}
                  handleEditionChangeSearchable={handleEditionChangeSearchable}
                  fileEdition={fileEdition}
                  switchEdition={switchEdition}
                />
              ))}
            </div>
            {(!editionsLoading && editions.length === 0) && (
              <FileSidebarEmptyState
                info={intl.formatMessage({ defaultMessage: 'Use the image editor to create a file edition' })}
                wiki="https://help.stockpress.co/en/articles/9341551-crop-assets-with-our-image-editor"
                video="https://player.vimeo.com/video/1063863519?h=2c58d8864a&badge=0&byline=0&title=0"
                title={intl.formatMessage({ defaultMessage: 'Image editor' })}
              />
            )}
          </>
        )}
        {!fileImageEditorSupported && (
          <FileSidebarEmptyState
            info={intl.formatMessage({ defaultMessage: 'File type not supported by Image Editor' })}
            wiki="https://help.stockpress.co/en/articles/9341551-crop-assets-with-our-image-editor"
            video="https://player.vimeo.com/video/1063863519?h=2c58d8864a&badge=0&byline=0&title=0"
            title={intl.formatMessage({ defaultMessage: 'Image editor' })}
          />
        )}
      </div>
    </div>
  );
};

const mapStateToProps = state => ({
  editions: state.Files.fileEditions,
  editionsLoading: state.Files.fileEditionsLoading,
});

const mapDispatchToProps = dispatch => ({
  handleFetchEditions: (fileUuid) => (
    dispatch(fetchFileEditions(fileUuid))
  ),
  handleSetEditions: (editions) => {
    dispatch(setFileEditions(editions));
  },
  handleImageEditor: (
    file,
    fileVersion,
    afterCreateVersion,
    afterCreateEdition,
    updateCommentsCount,
  ) => {
    dispatch(showModal('ImageEditorModal', {
      className: 'modal-image-editor',
      file,
      fileVersion,
      afterCreateVersion,
      afterCreateEdition,
      updateCommentsCount,
    }));
  },
});

FileEditions.propTypes = {
  file: PropTypes.object.isRequired,
  fileEdition: PropTypes.object,
  switchEdition: PropTypes.func.isRequired,
  handleFetchEditions: PropTypes.func.isRequired,
  handleSetEditions: PropTypes.func.isRequired,
  editions: PropTypes.array.isRequired,
  editionsLoading: PropTypes.bool.isRequired,
  handleFullEditionsLoaded: PropTypes.func.isRequired,
};

FileEditions.defaultProps = {
  fileEdition: null,
};

export default connect(mapStateToProps, mapDispatchToProps)(FileEditions);
